import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Car, Route, SlidersHorizontal, Plus, Pencil, ArrowUp, ArrowDown, Archive, RotateCcw, Loader2, Trash2, Check, Truck, Boxes, AlertOctagon, Replace, Fingerprint, HelpCircle } from "lucide-react";
import { refreshVehicleLists } from "@/lib/vehicleLists";
import { ComponentSuggestionsPanel } from "@/components/ComponentSuggestionsPanel";

const COLORS = ["slate", "blue", "sky", "emerald", "amber", "orange", "red", "indigo", "purple"];
const COLOR_DOT = { slate: "bg-slate-400", blue: "bg-blue-500", sky: "bg-sky-500", emerald: "bg-emerald-500", amber: "bg-amber-500", orange: "bg-orange-500", red: "bg-red-500", indigo: "bg-indigo-500", purple: "bg-purple-500" };

function reordered(list, idx, dir) {
  const j = idx + dir;
  if (j < 0 || j >= list.length) return null;
  const copy = [...list];
  [copy[idx], copy[j]] = [copy[j], copy[idx]];
  return copy.map((x) => x.id);
}

// ---------------------------------------------------------------- CATEGORIES
function CategoriesTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/vehicle-categories", { params: { include_archived: showArch } });
    setItems(data.items); refreshVehicleLists();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/vehicle-categories", { name: adding.trim() }); setAdding(""); await load(); toast.success("Category added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try { await api.put(`/vehicle-categories/${edit.id}`, { name: edit.name }); setEdit(null); await load(); toast.success("Renamed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (c, archived) => {
    try { await api.post(`/vehicle-categories/${c.id}/archive`, null, { params: { archived } }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/vehicle-categories/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid="cat-add-input" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New vehicle category (e.g. Motorcycle)…" className="rounded-lg h-10" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="cat-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="cat-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((c, i) => (
          <div key={c.id} data-testid={`cat-row-${c.code}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${c.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{c.name}</span>
              <span className="ml-2 font-mono text-[11px] text-slate-400">{c.code}</span>
              {c.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">archived</Badge>}
            </span>
            {canEdit && !c.archived && (
              <>
                <button data-testid={`cat-up-${c.code}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`cat-down-${c.code}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`cat-rename-${c.code}`} onClick={() => setEdit({ id: c.id, name: c.name })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                {c.code !== "uncategorized" && <button data-testid={`cat-archive-${c.code}`} onClick={() => archive(c, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>}
              </>
            )}
            {canEdit && c.archived && <button data-testid={`cat-restore-${c.code}`} onClick={() => archive(c, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename category</DialogTitle></DialogHeader>
          <Input data-testid="cat-rename-input" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="rounded-lg h-10" />
          <p className="text-xs text-slate-400">The internal code stays fixed — renaming never breaks spec assignments.</p>
          <DialogFooter><Button data-testid="cat-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- TYPES
// TYPE is the one vehicle list that never got a screen: it could only be added through a browser
// pop-up, never renamed, never reordered. It belongs here beside Categories, Stages and Spec
// Fields — they're all attributes of a vehicle the owner edits himself.
//
// A truck carries its type as a NAME, not an id, so a rename has a blast radius. The server
// rewrites the trucks in the same breath and reports the count; this screen states it up front
// rather than letting a rename quietly touch 94 records.
function TypesTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/truck-types", { params: { include_archived: showArch } });
    setItems(data || []); refreshVehicleLists();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/truck-types", { name: adding.trim() }); setAdding(""); await load(); toast.success("Type added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/truck-types/${edit.id}`, { name: edit.name });
      setEdit(null); await load();
      toast.success(data.trucks_updated
        ? `Renamed — ${data.trucks_updated} vehicle${data.trucks_updated === 1 ? "" : "s"} updated to “${data.name}”`
        : "Renamed");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (t, archived) => {
    try {
      const { data } = await api.post(`/truck-types/${t.id}/archive`, null, { params: { archived } });
      await load();
      if (archived && data.trucks_still_carrying_it) {
        toast.success(`Hidden from the pickers — ${data.trucks_still_carrying_it} vehicle(s) keep this type and stay filterable`);
      }
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/truck-types/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid="type-add-input" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New type (e.g. Rollback, Dump Truck)…" className="rounded-lg h-10" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="type-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="type-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((t, i) => (
          <div key={t.id} data-testid={`type-row-${t.name}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${t.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{t.name}</span>
              <span data-testid={`type-inuse-${t.name}`} className="ml-2 text-[11px] text-slate-400">
                {t.in_use} vehicle{t.in_use === 1 ? "" : "s"}
              </span>
              {t.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">archived</Badge>}
            </span>
            {canEdit && !t.archived && (
              <>
                <button data-testid={`type-up-${t.name}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`type-down-${t.name}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`type-rename-${t.name}`} onClick={() => setEdit({ id: t.id, name: t.name, in_use: t.in_use })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`type-archive-${t.name}`} onClick={() => archive(t, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && t.archived && <button data-testid={`type-restore-${t.name}`} onClick={() => archive(t, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No types yet. Add the ones your yard actually uses.</p>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename type</DialogTitle></DialogHeader>
          <Input data-testid="type-rename-input" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="rounded-lg h-10" />
          <p className="text-xs text-slate-500">
            {edit?.in_use
              ? <>A vehicle stores its type by name, so the <b>{edit.in_use}</b> vehicle{edit.in_use === 1 ? "" : "s"} carrying this type {edit.in_use === 1 ? "is" : "are"} renamed with it. Nothing is lost and they stay filterable.</>
              : <>Nothing carries this type yet, so the rename touches this list only.</>}
          </p>
          <DialogFooter><Button data-testid="type-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- STATUSES
function StatusesTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);   // {id?, name, color, is_terminal}

  const load = async () => {
    const { data } = await api.get("/donor-statuses", { params: { include_archived: showArch } });
    setItems(data.items); refreshVehicleLists();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.put(`/donor-statuses/${edit.id}`, { name: edit.name, color: edit.color, is_terminal: edit.is_terminal });
      else await api.post("/donor-statuses", { name: edit.name, color: edit.color, is_terminal: edit.is_terminal });
      setEdit(null); await load(); toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (s, archived) => {
    try { await api.post(`/donor-statuses/${s.id}/archive`, null, { params: { archived } }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => { const ids = reordered(items, idx, dir); if (!ids) return; await api.post("/donor-statuses/reorder", { ids }); await load(); };

  return (
    <div className="space-y-4">
      {canEdit && <Button data-testid="status-add-btn" onClick={() => setEdit({ name: "", color: "slate", is_terminal: false })} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add stage</Button>}
      <p className="text-xs text-slate-500">Order = the pipeline order trucks move through. Location (where a truck sits) is tracked separately by scanning.</p>
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="status-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((s, i) => (
          <div key={s.id} data-testid={`status-row-${s.code}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${s.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="font-mono text-[11px] text-slate-400 w-5 text-right">{s.sort}</span>
            <span className={`w-3 h-3 rounded-full ${COLOR_DOT[s.color] || "bg-slate-400"}`} />
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{s.name}</span>
              <span className="ml-2 font-mono text-[11px] text-slate-400">{s.code}</span>
              {s.is_terminal && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">terminal</Badge>}
              {s.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">archived</Badge>}
            </span>
            {canEdit && !s.archived && (
              <>
                <button data-testid={`status-up-${s.code}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`status-down-${s.code}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`status-edit-${s.code}`} onClick={() => setEdit({ id: s.id, name: s.name, color: s.color, is_terminal: s.is_terminal })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`status-archive-${s.code}`} onClick={() => archive(s, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && s.archived && <button data-testid={`status-restore-${s.code}`} onClick={() => archive(s, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>{edit?.id ? "Edit stage" : "New stage"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="status-name-input" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Stage name" className="rounded-lg h-10" />
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-1">Color</div>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} data-testid={`status-color-${c}`} onClick={() => setEdit({ ...edit, color: c })} className={`w-7 h-7 rounded-full ${COLOR_DOT[c]} ${edit?.color === c ? "ring-2 ring-offset-2 ring-slate-800" : ""}`} />
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="status-terminal-switch" checked={!!edit?.is_terminal} onCheckedChange={(v) => setEdit({ ...edit, is_terminal: v })} />Terminal stage (end of life — scrapped/archived)</label>
          </div>
          <DialogFooter><Button data-testid="status-save" onClick={save} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- SPEC FIELDS
const TYPES = [["number", "Number"], ["text", "Text"], ["enum", "Dropdown"], ["bool", "Yes / No"]];

function SpecFieldsTab({ canEdit, categories }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/vehicle-specs/fields", { params: { include_archived: showArch } });
    setItems(data.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const blank = () => ({ label: "", group: "General", type: "text", unit: "", applies_to: [], component: "", enum_options: [], searchable: true, cost_walled: false, public_crossover: true });
  const save = async () => {
    if (!edit.label.trim()) { toast.error("A field label is required."); return; }
    setBusy(true);
    const payload = { ...edit, enum_options: edit.type === "enum" ? edit.enum_options.filter((o) => o.label?.trim()) : [] };
    try {
      if (edit.id) await api.put(`/vehicle-specs/fields/${edit.id}`, payload);
      else await api.post("/vehicle-specs/fields", payload);
      setEdit(null); await load(); toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (f, archived) => { try { await api.post(`/vehicle-specs/fields/${f.id}/archive`, null, { params: { archived } }); await load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  // Order decides the order the spec panel renders these in. The endpoint existed; the screen
  // never offered it, so the list could only ever be in the order it was created.
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/vehicle-specs/fields/reorder", { ids }); await load();
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.label}" from the registry? This permanently deletes the field (only allowed when no truck holds a value for it).`)) return;
    try { await api.delete(`/vehicle-specs/fields/${f.id}`); toast.success("Field removed"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleCat = (code) => setEdit((e) => ({ ...e, applies_to: e.applies_to.includes(code) ? e.applies_to.filter((c) => c !== code) : [...e.applies_to, code] }));
  const [compGroups, setCompGroups] = useState([]);
  useEffect(() => { api.get("/component-groups").then((r) => setCompGroups(r.data.items || [])).catch(() => {}); }, []);

  const activeCats = categories.filter((c) => !c.archived && c.code !== "uncategorized");
  const catName = (code) => categories.find((c) => c.code === code)?.name || code;

  return (
    <div className="space-y-4">
      {canEdit && <Button data-testid="spec-add-btn" onClick={() => setEdit(blank())} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add spec field</Button>}
      {!canEdit && <p className="text-xs text-amber-600">Editing spec structure requires the "Manage vehicle spec structure" permission.</p>}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="spec-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((f, i) => (
          <div key={f.id} data-testid={`spec-row-${f.key}`} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${f.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{f.label}</span>
              <span className="ml-2 text-[11px] text-slate-400">{f.group} · {TYPES.find((t) => t[0] === f.type)?.[1]}{f.unit ? ` (${f.unit})` : ""}</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(f.applies_to.length ? f.applies_to : ["all categories"]).map((c) => <Badge key={c} className="bg-slate-100 text-slate-600 text-[10px] font-normal">{f.applies_to.length ? catName(c) : c}</Badge>)}
                {f.component && <Badge className="bg-violet-50 text-violet-700 text-[10px] capitalize">{f.component.replace(/_/g, " ")}</Badge>}
                {f.searchable && <Badge className="bg-sky-50 text-sky-700 text-[10px]">searchable</Badge>}
                {f.cost_walled && <Badge className="bg-amber-50 text-amber-700 text-[10px]">cost-walled</Badge>}
                {f.public_crossover && <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">public-eligible</Badge>}
                {f.archived && <Badge className="bg-slate-200 text-slate-600 text-[10px]">archived</Badge>}
              </div>
            </span>
            {canEdit && !f.archived && (
              <div className="flex items-center gap-0.5 shrink-0">
                <button data-testid={`spec-up-${f.key}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`spec-down-${f.key}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`spec-edit-${f.key}`} onClick={() => setEdit({ ...f, unit: f.unit || "", enum_options: f.enum_options || [] })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`spec-archive-${f.key}`} title="Archive / hide (keeps recorded values)" onClick={() => archive(f, true)} className="p-1 text-slate-500 hover:text-amber-600"><Archive className="w-4 h-4" /></button>
                <button data-testid={`spec-remove-${f.key}`} title="Remove permanently (only if unused)" onClick={() => remove(f)} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
            {canEdit && f.archived && <button data-testid={`spec-restore-${f.key}`} onClick={() => archive(f, false)} className="p-1 text-slate-500 hover:text-emerald-600 shrink-0"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="spec-edit-dialog" className="rounded-lg max-w-lg max-h-kb overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit spec field" : "New spec field"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Input data-testid="spec-label-input" value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} placeholder="Label (e.g. Engine HP)" className="rounded-lg h-10" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="spec-group-input" value={edit.group} onChange={(e) => setEdit({ ...edit, group: e.target.value })} placeholder="Group (section)" className="rounded-lg h-10" />
                <Input data-testid="spec-unit-input" value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value })} placeholder="Unit (hp, lb, in…)" className="rounded-lg h-10" />
              </div>
              <Select value={edit.type} onValueChange={(v) => setEdit({ ...edit, type: v })}>
                <SelectTrigger data-testid="spec-type-select" className="rounded-lg h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`spec-type-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {edit.type === "enum" && (
                <div className="rounded-lg border border-slate-200 p-2 space-y-1.5 bg-slate-50">
                  <div className="text-xs font-bold uppercase text-slate-500">Dropdown options</div>
                  {edit.enum_options.map((o, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input data-testid={`spec-enum-${idx}`} value={o.label} onChange={(e) => { const arr = [...edit.enum_options]; arr[idx] = { ...arr[idx], label: e.target.value }; setEdit({ ...edit, enum_options: arr }); }} placeholder="Option label" className="rounded-lg h-9 bg-white" />
                      <button onClick={() => setEdit({ ...edit, enum_options: edit.enum_options.filter((_, k) => k !== idx) })} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button data-testid="spec-enum-add" size="sm" variant="outline" onClick={() => setEdit({ ...edit, enum_options: [...edit.enum_options, { label: "" }] })} className="rounded-lg h-8"><Plus className="w-3.5 h-3.5 mr-1" />Add option</Button>
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">Applies to categories <span className="font-normal normal-case text-slate-400">(none = all)</span></div>
                <div className="flex flex-wrap gap-1.5">
                  {activeCats.map((c) => (
                    <button key={c.code} data-testid={`spec-applies-${c.code}`} onClick={() => toggleCat(c.code)}
                      className={`text-xs px-2.5 py-1.5 rounded-lg border ${edit.applies_to.includes(c.code) ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-500"}`}>
                      {edit.applies_to.includes(c.code) && <Check className="w-3 h-3 inline mr-1" />}{c.name}
                    </button>
                  ))}
                </div>
              </div>
              {/* WHICH COMPONENT BLOCK THIS FIELD BELONGS TO. The API always accepted it; no
                  screen ever offered it, so a new component group had no way to hold a field. */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">Component block <span className="font-normal normal-case text-slate-400">(optional — groups this field with a component's numbers and its condition rating)</span></div>
                <Select value={edit.component || "__none__"} onValueChange={(v) => setEdit({ ...edit, component: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="spec-component-select" className="rounded-lg h-10"><SelectValue placeholder="Not tied to a component" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="__none__">Not tied to a component</SelectItem>
                    {compGroups.map((g) => <SelectItem key={g.code} value={g.code} data-testid={`spec-component-opt-${g.code}`}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 pt-1">
                <label className="flex items-center justify-between text-sm text-slate-700"><span>Searchable (feeds spec filter)</span><Switch data-testid="spec-searchable" checked={edit.searchable} onCheckedChange={(v) => setEdit({ ...edit, searchable: v })} /></label>
                <label className="flex items-center justify-between text-sm text-slate-700"><span>Cost-walled (only cost-authorized roles)</span><Switch data-testid="spec-costwalled" checked={edit.cost_walled} onCheckedChange={(v) => setEdit({ ...edit, cost_walled: v })} /></label>
                <label className="flex items-center justify-between text-sm text-slate-700"><span>Public-eligible (can cross to public listing when confirmed)</span><Switch data-testid="spec-public" checked={edit.public_crossover} onCheckedChange={(v) => setEdit({ ...edit, public_crossover: v })} /></label>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="spec-save" onClick={save} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- COMPONENT GROUPS
// The blocks every truck's specs, tag numbers and CONDITION ratings hang off. The endpoints for
// this list existed from the start and no screen ever called them, so the ten seeded groups were
// effectively hardcoded — a group he couldn't add was a component he couldn't assess.
function ComponentGroupsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/component-groups", { params: { include_archived: showArch } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/component-groups", { name: adding.trim() }); setAdding(""); await load(); toast.success("Component added — it shows up on every truck straight away"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/component-groups/${edit.id}`, { name: edit.name });
      setEdit(null); await load();
      toast.success(data.trucks_carried_over
        ? `Renamed — ${data.trucks_carried_over} vehicle(s) carried over with it`
        : "Renamed");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (g, archived) => {
    try { await api.post(`/component-groups/${g.id}/archive`, null, { params: { archived } }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/component-groups/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        A component group is a block on every truck: its identifying specs, its tag numbers, and its
        condition rating. Add one here and it appears on the truck page, the Floor rating screen and
        the condition filters immediately — assign spec fields to it on the <b>Spec Fields</b> tab.
      </p>
      {canEdit && <ComponentSuggestionsPanel />}
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid="cg-add-input" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New component (e.g. Hydraulics, Wet Kit)…" className="rounded-lg h-10" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="cg-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="cg-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((g, i) => (
          <div key={g.id} data-testid={`cg-row-${g.code}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${g.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{g.name}</span>
              <span data-testid={`cg-usage-${g.code}`} className="ml-2 text-[11px] text-slate-400">
                {g.spec_fields} spec field{g.spec_fields === 1 ? "" : "s"} · {g.trucks_with_numbers} with numbers · {g.trucks_assessed} rated
              </span>
              {g.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">archived</Badge>}
            </span>
            {canEdit && !g.archived && (
              <>
                <button data-testid={`cg-up-${g.code}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`cg-down-${g.code}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`cg-rename-${g.code}`} onClick={() => setEdit({ id: g.id, name: g.name })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`cg-archive-${g.code}`} onClick={() => archive(g, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && g.archived && <button data-testid={`cg-restore-${g.code}`} onClick={() => archive(g, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Renaming carries every truck with it (specs and ratings point at the code, never the wording).
        Archiving is refused while spec fields, tag numbers or ratings still hang off it — the same
        rule as a stage that still has trucks at it.
      </p>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename component</DialogTitle></DialogHeader>
          <Input data-testid="cg-rename-input" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="rounded-lg h-10" />
          <p className="text-xs text-slate-500">Specs, numbers and condition ratings follow the rename — they point at the group's code, not its wording.</p>
          <DialogFooter><Button data-testid="cg-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- CONDITION REASONS
// The words behind KNOWN BAD. One tap at the truck, gloves on — free text only behind "Other",
// and if the same word keeps coming back as Other, that's the signal to put it on the list.
function ConditionReasonsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [other, setOther] = useState(null);

  const load = async () => {
    const { data } = await api.get("/condition-reasons", { params: { include_archived: showArch } });
    setItems(data.items || []);
    api.get("/condition-coverage").then((r) => setOther(r.data.trucks_with_other_reason)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/condition-reasons", { label: adding.trim() }); setAdding(""); await load(); toast.success("Reason added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/condition-reasons/${edit.id}`, { label: edit.label });
      setEdit(null); await load();
      toast.success(data.assessments_showing_it
        ? `Renamed — ${data.assessments_showing_it} vehicle(s) showing this reason now read “${data.label}”`
        : "Renamed");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (r, archived) => {
    try {
      const { data } = await api.post(`/condition-reasons/${r.id}/archive`, null, { params: { archived } });
      await load();
      if (archived) toast.success(`Stopped being offered — ${data.assessments_keeping_it} vehicle(s) keep it and stay searchable by it`);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/condition-reasons/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Why a component is KNOWN BAD, in one tap at the truck. Keep it to words your crew actually
        says — a list of thirty nobody uses is as useless as five that don't cover it.
      </p>
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid="cr-add-input" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New reason word (e.g. Bent, Water damage)…" className="rounded-lg h-10" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="cr-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="cr-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((r, i) => (
          <div key={r.id} data-testid={`cr-row-${r.code}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${r.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{r.label}</span>
              {r.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">not offered</Badge>}
            </span>
            {canEdit && !r.archived && (
              <>
                <button data-testid={`cr-up-${r.code}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`cr-down-${r.code}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`cr-rename-${r.code}`} onClick={() => setEdit({ id: r.id, label: r.label })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`cr-archive-${r.code}`} onClick={() => archive(r, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && r.archived && <button data-testid={`cr-restore-${r.code}`} onClick={() => archive(r, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Archiving behaves like archiving a spec field: every truck already marked with the word keeps
        it and stays searchable by it — the word just stops being offered for new ratings.
        {other ? <> · <b data-testid="cr-other-count">{other} vehicle(s)</b> were rated with a free-text “Other” — if the same word keeps coming back, put it on the list properly.</> : null}
      </p>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename reason</DialogTitle></DialogHeader>
          <Input data-testid="cr-rename-input" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="rounded-lg h-10" />
          <p className="text-xs text-slate-500">Assessments store the code, so every truck showing this reason follows the rename. Nothing is stranded.</p>
          <DialogFooter><Button data-testid="cr-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// WHY AN ASK WAS CLOSED WITHOUT AN ANSWER — his words, not ours (owner ruling 2026-09-29: "same
// as every other reason list, one mechanism, not a new one"). Identical shape to the KNOWN BAD
// words above: add, rename, reorder, archive, all four his, same `manage_lists` gate. One
// difference, and it is a safety rule rather than a new idea: "Other" can be renamed but never
// archived, because the free-text path is keyed on its code.
function AskReasonsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/ask-reasons", { params: { include_archived: showArch } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/ask-reasons", { label: adding.trim() }); setAdding(""); await load(); toast.success("Reason added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/ask-reasons/${edit.id}`, { label: edit.label });
      setEdit(null); await load();
      toast.success(data.closed_asks_keeping_their_wording
        ? `Renamed — ${data.closed_asks_keeping_their_wording} already-closed ask(s) keep the words the asker was told`
        : "Renamed");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (r, archived) => {
    try {
      const { data } = await api.post(`/ask-reasons/${r.id}/archive`, null, { params: { archived } });
      await load();
      if (archived) toast.success(`Stopped being offered — ${data.closed_asks_keeping_it} closed ask(s) keep it`);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/ask-reasons/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        When somebody asks about a component and the question goes away without anyone rating it —
        the customer bought elsewhere, it was the wrong truck, the truck is gone. Whoever asked is
        told the reason you pick here, so keep it to words you'd be happy for them to read.
      </p>
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid="ar-add-input" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New closing reason (e.g. Part already sold)…" className="rounded-lg h-10" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="ar-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="ar-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((r, i) => (
          <div key={r.id} data-testid={`ar-row-${r.code}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${r.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{r.label}</span>
              {r.reserved && <Badge data-testid={`ar-reserved-${r.code}`} className="ml-2 bg-slate-100 text-slate-600 text-[10px]">always offered · free text</Badge>}
              {r.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">not offered</Badge>}
            </span>
            {canEdit && !r.archived && (
              <>
                <button data-testid={`ar-up-${r.code}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`ar-down-${r.code}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`ar-rename-${r.code}`} onClick={() => setEdit({ id: r.id, label: r.label })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                {/* ABSENT, not greyed, on the one row that must stay */}
                {!r.reserved && <button data-testid={`ar-archive-${r.code}`} onClick={() => archive(r, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>}
              </>
            )}
            {canEdit && r.archived && <button data-testid={`ar-restore-${r.code}`} onClick={() => archive(r, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Renaming is safe: a closed ask keeps the exact words the asker was told, and new closes
        follow the new wording. Archiving only stops a word being offered. <b>"Other" stays</b> — it
        is the only way to close an ask for a reason nobody listed, and it asks for a word or two of
        free text when it is used.
      </p>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename closing reason</DialogTitle></DialogHeader>
          <Input data-testid="ar-rename-input" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="rounded-lg h-10" />
          <p className="text-xs text-slate-500">Asks already closed keep the wording their asker was told. Nothing is rewritten behind anybody's back.</p>
          <DialogFooter><Button data-testid="ar-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// WHY A PHOTO WAS WRONG — his list, not ours. Same mechanism as the KNOWN BAD words: add, rename,
// reorder, archive, all four his. Two of them OFFER A MOVE instead of a removal, because
// "archiving a good photo that was simply on the wrong truck loses it; moving it keeps it".
function PhotoReasonsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [addAction, setAddAction] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [otherUsed, setOtherUsed] = useState(0);

  const load = async () => {
    const { data } = await api.get("/photo-reasons", { params: { include_archived: showArch } });
    setItems(data.items || []);
    setOtherUsed(data.other_used || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try { await api.post("/photo-reasons", { label: adding.trim(), action: addAction || null }); setAdding(""); setAddAction(""); await load(); toast.success("Reason added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/photo-reasons/${edit.id}`, { label: edit.label, action: edit.action || null });
      setEdit(null); await load();
      toast.success(data.corrections_showing_it
        ? `Renamed — ${data.corrections_showing_it} logged correction(s) now read “${data.label}”`
        : "Renamed");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (r, archived) => {
    try {
      const { data } = await api.post(`/photo-reasons/${r.id}/archive`, null, { params: { archived } });
      await load();
      if (archived) toast.success(`Stopped being offered — ${data.corrections_keeping_it} logged correction(s) keep it`);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/photo-reasons/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4" data-testid="photo-reasons-tab">
      <p className="text-xs text-slate-500">
        What somebody taps when a photo is on the wrong record. Two of these can lead straight to a
        MOVE instead of a removal — a good photo filed in the wrong place should be relocated, not lost.
      </p>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Input data-testid="pr-add-input" value={adding} onChange={(e) => setAdding(e.target.value)}
            placeholder="New reason (e.g. Someone's hand in the shot)…" className="rounded-lg h-10 flex-1 min-w-[16rem]"
            onKeyDown={(e) => e.key === "Enter" && add()} />
          <select data-testid="pr-add-action" value={addAction} onChange={(e) => setAddAction(e.target.value)}
            className="rounded-lg h-10 border border-slate-300 text-sm px-2 bg-white">
            <option value="">just remove it</option>
            <option value="move_truck">offer moving it to the right truck</option>
            <option value="move_part">offer moving it to the right part</option>
          </select>
          <Button data-testid="pr-add-btn" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)} data-testid="pr-show-archived" />Show archived</label>
      <div className="space-y-1.5">
        {items.map((r, i) => (
          <div key={r.id} data-testid={`pr-row-${r.code.replace(/[^a-z]+/g, "-")}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${r.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{r.label}</span>
              {r.action === "move_truck" && <Badge className="ml-2 bg-orange-100 text-[#C76714] text-[10px]">offers a truck move</Badge>}
              {r.action === "move_part" && <Badge className="ml-2 bg-orange-100 text-[#C76714] text-[10px]">offers a part move</Badge>}
              {r.reserved && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">reserved · needs a note</Badge>}
              {r.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">not offered</Badge>}
              {r.used_count > 0 && <span className="ml-2 text-[11px] text-slate-400">used {r.used_count}×</span>}
            </span>
            {canEdit && !r.archived && !r.reserved && (
              <>
                <button data-testid={`pr-up-${r.code.replace(/[^a-z]+/g, "-")}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`pr-down-${r.code.replace(/[^a-z]+/g, "-")}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`pr-rename-${r.code.replace(/[^a-z]+/g, "-")}`} onClick={() => setEdit({ id: r.id, label: r.label, action: r.action || "" })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`pr-archive-${r.code.replace(/[^a-z]+/g, "-")}`} onClick={() => archive(r, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && r.archived && <button data-testid={`pr-restore-${r.code.replace(/[^a-z]+/g, "-")}`} onClick={() => archive(r, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Corrections store the code, so renaming a word follows through every logged correction.
        {otherUsed ? <> · <b data-testid="pr-other-count">“Other” has been used {otherUsed}×</b> — if the same thing keeps coming back in the note, put it on the list properly.</> : <> · “Other” always stays, behind a written note, and its count shows here.</>}
      </p>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename reason</DialogTitle></DialogHeader>
          <Input data-testid="pr-rename-input" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="rounded-lg h-10" />
          <select data-testid="pr-rename-action" value={edit?.action || ""} onChange={(e) => setEdit({ ...edit, action: e.target.value })}
            className="rounded-lg h-10 border border-slate-300 text-sm px-2 bg-white">
            <option value="">just remove it</option>
            <option value="move_truck">offer moving it to the right truck</option>
            <option value="move_part">offer moving it to the right part</option>
          </select>
          <DialogFooter><Button data-testid="pr-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// HOW SOMEBODY KNOWS WHICH TRUCK A LEGACY PART CAME OFF — his third list, deliberately on this
// same screen: "three reason lists in three different screens is the scattering I've spent the
// month removing." Each word is EVIDENCE or JUDGEMENT, because "someone reading the part's history
// later should be able to tell a documented match from a memory", and a word can be marked as
// needing a NAME — the same rule as "reported by Dave" on component condition.
function SourceReasonsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [showArch, setShowArch] = useState(false);
  const [adding, setAdding] = useState("");
  const [addKind, setAddKind] = useState("judgement");
  const [addName, setAddName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [otherUsed, setOtherUsed] = useState(0);
  const slug = (c) => c.replace(/[^a-z]+/g, "-");

  const load = async () => {
    const { data } = await api.get("/source-reasons", { params: { include_archived: showArch } });
    setItems(data.items || []);
    setOtherUsed(data.other_used || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArch]);

  const add = async () => {
    if (!adding.trim()) return;
    setBusy(true);
    try {
      await api.post("/source-reasons", { label: adding.trim(), kind: addKind, needs_name: addName });
      setAdding(""); setAddKind("judgement"); setAddName(false); await load();
      toast.success("Way of knowing added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/source-reasons/${edit.id}`,
        { label: edit.label, kind: edit.kind, needs_name: edit.needs_name });
      setEdit(null); await load();
      toast.success(data.parts_showing_it
        ? `Renamed — ${data.parts_showing_it} part(s) now read “${data.label}”`
        : "Renamed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const archive = async (r, archived) => {
    try {
      const { data } = await api.post(`/source-reasons/${r.id}/archive`, null, { params: { archived } });
      await load();
      if (archived) toast.success(`Stopped being offered — ${data.parts_keeping_it} part(s) keep it`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = reordered(items, idx, dir); if (!ids) return;
    await api.post("/source-reasons/reorder", { ids }); await load();
  };

  return (
    <div className="space-y-4" data-testid="source-reasons-tab">
      <p className="text-xs text-slate-500">
        What somebody taps when they record which truck a legacy part came off. An assignment with
        no evidence behind it is a guess wearing a truck number — so mark each word as DOCUMENTED
        (there's a record behind it) or JUDGEMENT (somebody's recognition), and tick “needs a name”
        on any word that means somebody told them.
      </p>
      {canEdit && (
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="sr-add-input" value={adding} onChange={(e) => setAdding(e.target.value)}
            placeholder="New way of knowing (e.g. Casting number matches)…"
            className="rounded-lg h-10 flex-1 min-w-[16rem]" onKeyDown={(e) => e.key === "Enter" && add()} />
          <select data-testid="sr-add-kind" value={addKind} onChange={(e) => setAddKind(e.target.value)}
            className="rounded-lg h-10 border border-slate-300 text-sm px-2 bg-white">
            <option value="documented">documented evidence</option>
            <option value="judgement">somebody's judgement</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" data-testid="sr-add-needs-name" checked={addName}
              onChange={(e) => setAddName(e.target.checked)} />needs a name
          </label>
          <Button data-testid="sr-add-btn" onClick={add} disabled={busy}
            className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-10"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500">
        <input type="checkbox" checked={showArch} onChange={(e) => setShowArch(e.target.checked)}
          data-testid="sr-show-archived" />Show archived
      </label>
      <div className="space-y-1.5">
        {items.map((r, i) => (
          <div key={r.id} data-testid={`sr-row-${slug(r.code)}`}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${r.archived ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{r.label}</span>
              <Badge className={`ml-2 text-[10px] ${r.kind === "documented" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                {r.kind === "documented" ? "documented" : "judgement"}
              </Badge>
              {r.needs_name && <Badge className="ml-2 bg-amber-100 text-amber-900 text-[10px]">needs a name</Badge>}
              {r.reserved && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">reserved · needs a note</Badge>}
              {r.archived && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">not offered</Badge>}
              {r.used_count > 0 && <span className="ml-2 text-[11px] text-slate-400">used {r.used_count}×</span>}
            </span>
            {canEdit && !r.archived && !r.reserved && (
              <>
                <button data-testid={`sr-up-${slug(r.code)}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`sr-down-${slug(r.code)}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`sr-rename-${slug(r.code)}`} onClick={() => setEdit({ id: r.id, label: r.label, kind: r.kind, needs_name: r.needs_name })} className="p-1 text-slate-500 hover:text-[#E0781A]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`sr-archive-${slug(r.code)}`} onClick={() => archive(r, true)} className="p-1 text-slate-500 hover:text-red-600"><Archive className="w-4 h-4" /></button>
              </>
            )}
            {canEdit && r.archived && <button data-testid={`sr-restore-${slug(r.code)}`} onClick={() => archive(r, false)} className="p-1 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Parts store the code, so renaming a word follows through every part already recorded with it.
        {otherUsed ? <> · <b data-testid="sr-other-count">“Other” has been used {otherUsed}×</b> — if the same way of knowing keeps coming back in the note, put it on the list properly.</> : <> · “Other” always stays, behind a written note, and its count shows here.</>}
      </p>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-lg"><DialogHeader><DialogTitle>Rename this way of knowing</DialogTitle></DialogHeader>
          <Input data-testid="sr-rename-input" value={edit?.label || ""} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="rounded-lg h-10" />
          <select data-testid="sr-rename-kind" value={edit?.kind || "judgement"} onChange={(e) => setEdit({ ...edit, kind: e.target.value })}
            className="rounded-lg h-10 border border-slate-300 text-sm px-2 bg-white">
            <option value="documented">documented evidence</option>
            <option value="judgement">somebody's judgement</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" data-testid="sr-rename-needs-name" checked={!!edit?.needs_name}
              onChange={(e) => setEdit({ ...edit, needs_name: e.target.checked })} />needs a name
          </label>
          <DialogFooter><Button data-testid="sr-rename-save" onClick={rename} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function VehicleSetupPage() {
  const { has } = useAuth();
  const canLists = has("manage_lists");
  const canSpecs = has("manage_vehicle_specs");
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/vehicle-categories", { params: { include_archived: true } }).then((r) => setCategories(r.data.items)).catch(() => {}); }, []);

  return (
    <div data-testid="vehicle-setup-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Vehicle Setup</h1>
        <p className="text-sm text-slate-500 mt-1">Your owner-controlled lists: vehicle categories, dismantle stages, types, component groups, the words behind KNOWN BAD, the words for closing an ask without an answer, and the spec-field registry that drives every truck's structured info. Add, rename, reorder, archive — all four are yours on every list here, nothing here is hard-coded and nothing needs a developer.</p>
        <p className="text-xs text-slate-400 mt-1">Yard places (sites, zones/sectors, shelves and spots) are not a list — they're a tree, and they live on <a href="/fixed-locations" className="text-[#C76714] underline hover:no-underline" data-testid="vehicle-setup-locations-link">Fixed Locations</a>.</p>
      </div>
      <Tabs defaultValue="categories">
        <TabsList data-testid="vehicle-setup-tabs" className="rounded-lg">
          <TabsTrigger value="categories" data-testid="tab-categories"><Car className="w-4 h-4 mr-1.5" />Categories</TabsTrigger>
          <TabsTrigger value="statuses" data-testid="tab-statuses"><Route className="w-4 h-4 mr-1.5" />Stages</TabsTrigger>
          <TabsTrigger value="specs" data-testid="tab-specs"><SlidersHorizontal className="w-4 h-4 mr-1.5" />Spec Fields</TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-types"><Truck className="w-4 h-4 mr-1.5" />Types</TabsTrigger>
          <TabsTrigger value="components" data-testid="tab-components"><Boxes className="w-4 h-4 mr-1.5" />Components</TabsTrigger>
          <TabsTrigger value="reasons" data-testid="tab-reasons"><AlertOctagon className="w-4 h-4 mr-1.5" />Bad Reasons</TabsTrigger>
          <TabsTrigger value="photo-reasons" data-testid="tab-photo-reasons"><Replace className="w-4 h-4 mr-1.5" />Photo Fixes</TabsTrigger>
          <TabsTrigger value="source-reasons" data-testid="tab-source-reasons"><Fingerprint className="w-4 h-4 mr-1.5" />Source Proof</TabsTrigger>
          <TabsTrigger value="ask-reasons" data-testid="tab-ask-reasons"><HelpCircle className="w-4 h-4 mr-1.5" />Ask Closing</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-4"><CategoriesTab canEdit={canLists} /></TabsContent>
        <TabsContent value="statuses" className="mt-4"><StatusesTab canEdit={canLists} /></TabsContent>
        <TabsContent value="specs" className="mt-4"><SpecFieldsTab canEdit={canSpecs} categories={categories} /></TabsContent>
        <TabsContent value="types" className="mt-4"><TypesTab canEdit={canLists || has("manage_taxonomy")} /></TabsContent>
        <TabsContent value="components" className="mt-4"><ComponentGroupsTab canEdit={canLists} /></TabsContent>
        <TabsContent value="reasons" className="mt-4"><ConditionReasonsTab canEdit={canLists} /></TabsContent>
        <TabsContent value="photo-reasons" className="mt-4"><PhotoReasonsTab canEdit={canLists} /></TabsContent>
        <TabsContent value="source-reasons" className="mt-4"><SourceReasonsTab canEdit={canLists} /></TabsContent>
        <TabsContent value="ask-reasons" className="mt-4"><AskReasonsTab canEdit={canLists} /></TabsContent>
      </Tabs>
    </div>
  );
}
